import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { DashboardHeader } from "../../components/dashboard-header"
import { DashboardShell } from "../../components/dashboard-shell"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { useToast } from "../../components/ui/use-toast"
import { supabase } from "@/utils/supabase/client"
import { useAuth } from "@/contexts/auth-context"
import { Organization, Subscription } from "@/types"

const plans = [
  { name: 'Pro', plan: 'pro_monthly', price: 5000, features: ['Up to 10 doctors', 'Up to 500 patients', 'Full feature access', 'Email support'] },
  { name: 'Enterprise', plan: 'enterprise_custom', price: null, features: ['Unlimited doctors & patients', 'Dedicated support & SLA', 'Custom integrations', 'On-premise option'] },
]

export default function OnboardingPage() {
  const [step, setStep] = useState(1)
  const [org, setOrg] = useState<Organization | null>(null)
  const [name, setName] = useState("")
  const [plan, setPlan] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { toast } = useToast()
  const { user, refreshOrganizations } = useAuth()
  const navigate = useNavigate()

  useEffect(() => { 
    if (user) {
      fetchOrganization() 
    }
  }, [user])

  const fetchOrganization = async () => {
    if (!user) return
    
    setLoading(true)
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('owner_id', user.id)
    
    if (error) {
      setLoading(false);
      // It's better to navigate away or show a clear error message
      // if the organization query fails, but for now, we'll just log it.
      console.error("Error fetching organization:", error);
      return;
    }
    
    if (data && data.length > 0) {
      // User already has an organization, redirect to dashboard.
      // This is a failsafe, as OnboardingGuard should prevent this page from loading.
      navigate('/app/dashboard');
    } else {
      // No organization found, allow user to create one.
      setLoading(false);
    }
  }

  const handleOrgSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!user) return
    
    setSaving(true)
    
    try {
      if (org) {
        const { error } = await supabase
          .from('organizations')
          .update({ name })
          .eq('id', org.id)
        
        if (error) throw error
        
        await refreshOrganizations(user.id)
        toast({ title: "Organization updated", description: "Your organization details have been updated." })
        setStep(2)
      } else {
        const { data: newOrg, error } = await supabase
          .from('organizations')
          .insert({ name, owner_id: user.id })
          .select('*')
          .single()
        
        if (error) throw error
        
        if (newOrg) {
          setOrg(newOrg) // Keep local state for immediate UI updates
          await refreshOrganizations(user.id) // Refresh global state
          toast({ title: "Organization created", description: "Your organization has been created." })
          setStep(2)
        }
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "An unexpected error occurred.", variant: "destructive" })
    } finally {
      setSaving(false)
    }
  }

  const handlePlanSelect = async (planId: string) => {
    if (!org) {
      toast({ title: "Error", description: "No organization selected. Please go back and create one.", variant: "destructive"})
      return
    }
    setSaving(true)
    
    try {
      const { error } = await supabase
        .from('subscriptions')
        .insert({ 
          organization_id: org.id, 
          plan: planId, 
          status: 'active', 
          start_date: new Date().toISOString() 
        })
      
      if (error) throw error

      toast({ 
        title: "Welcome to Clinic365!", 
        description: `You are now subscribed to the ${planId} plan. Let's get started!` 
      })
      
      // Navigate to dashboard after successful subscription
      navigate('/app/dashboard')
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to subscribe", variant: "destructive" })
    } finally {
      setSaving(false)
    }
  }

  const handleSkipPlan = () => {
    toast({ 
      title: "Setup Complete!", 
      description: "You can always upgrade your plan later from settings." 
    })
    navigate('/app/dashboard')
  }

  return (
    <div className="md:ml-64">
      <DashboardShell>
        <DashboardHeader 
          heading="Welcome to Clinic365" 
          text="Let's get your clinic set up in just a few steps" 
        />
        <div className="max-w-2xl mx-auto mt-8">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : step === 1 ? (
            <div className="bg-white rounded-lg shadow-sm border p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Setup Your Organization</h2>
                <p className="text-gray-600 mt-2">Step 1 of 2: Tell us about your clinic or practice</p>
              </div>
              
              <form onSubmit={handleOrgSubmit} className="space-y-6">
                <div>
                  <label htmlFor="org-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name
                  </label>
                  <Input
                    id="org-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Enter your clinic or organization name"
                    required
                    className="h-11"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This will be displayed in your dashboard and reports
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700" 
                  disabled={saving || !name.trim()}
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                      {org ? "Updating..." : "Creating..."}
                    </span>
                  ) : (
                    org ? "Update & Continue" : "Create & Continue"
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">Choose Your Plan</h2>
                <p className="text-gray-600 mt-2">Step 2 of 2: Select a plan that fits your needs</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {plans.map((p) => (
                  <div 
                    key={p.plan} 
                    className={`relative border rounded-lg p-6 hover:shadow-md transition-shadow ${
                      plan === p.plan ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xl font-bold text-gray-900">{p.name}</span>
                      {p.price && (
                        <span className="text-2xl font-bold text-gray-900">
                          Rs. {p.price.toLocaleString()}
                          <span className="text-sm text-gray-500 font-normal">/mo</span>
                        </span>
                      )}
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {p.features.map(f => (
                        <li key={f} className="flex items-center text-sm text-gray-600">
                          <svg className="h-4 w-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {f}
                        </li>
                      ))}
                    </ul>
                    
                    <Button
                      className="w-full"
                      onClick={() => handlePlanSelect(p.plan)}
                      disabled={saving}
                      variant={plan === p.plan ? 'default' : 'outline'}
                    >
                      {saving && plan === p.plan ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                          Processing...
                        </span>
                      ) : (
                        `Choose ${p.name}`
                      )}
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <Button 
                  variant="ghost" 
                  onClick={handleSkipPlan}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Skip for now - I'll choose later
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  You can always upgrade or change your plan from settings
                </p>
              </div>
            </div>
          )}
        </div>
      </DashboardShell>
    </div>
  )
} 